import React, { useEffect, useState } from "react";
import StaffLayout from "@/components/StaffLayout";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { AlertTriangle, CheckCircle2, XCircle, Clock, Loader2, RefreshCw } from "lucide-react";

const STATUS_META = {
  auto_approved: { label: "Auto-aprobado", cls: "bg-emerald-500/20 text-emerald-300" },
  pending_review: { label: "Pendiente", cls: "bg-amber-500/20 text-amber-300" },
  approved: { label: "Aprobado", cls: "bg-emerald-500/20 text-emerald-300" },
  processed: { label: "Procesado", cls: "bg-emerald-500/20 text-emerald-300" },
  rejected: { label: "Rechazado", cls: "bg-red-500/20 text-red-300" },
};
const REASON_LABEL = {
  no_encaja: "No me encaja",
  contenido_no_lo_esperado: "Contenido no fue lo esperado",
  problema_tecnico: "Problema técnico",
  otro: "Otro",
};

export default function StaffReembolsos() {
  const { user } = useAuth();
  const isSuper = user?.roles?.includes("superadmin_dcc");
  const [items, setItems] = useState([]);
  const [status, setStatus] = useState("pending_review");
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const q = status === "all" ? "" : `?status=${status}`;
      const r = await api.get(`/admin/refund-requests${q}`);
      setItems(r.data.items || []);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [status]);

  const resolve = async (id, action) => {
    let notes = "";
    if (action === "reject") {
      notes = prompt("Motivo del rechazo (obligatorio):") || "";
      if (!notes.trim()) return;
    } else {
      notes = prompt("Notas internas (opcional):") || "";
    }
    try {
      await api.put(`/admin/refund-requests/${id}`, { action, admin_notes: notes });
      toast.success(action === "approve" ? "Reembolso aprobado y procesado." : "Solicitud rechazada.");
      setSelected(null);
      load();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  };

  return (
    <StaffLayout user={user} superadmin={isSuper}>
      <div className="mb-6 flex items-center justify-between flex-wrap gap-3">
        <div>
          <p className="text-xs uppercase tracking-widest text-[#F26A21] font-bold">Post-venta</p>
          <h1 className="mt-1 text-3xl font-bold text-white font-display">Reembolsos</h1>
          <p className="mt-1 text-sm text-gray-400">Aprueba, rechaza o audita solicitudes de reembolso de alumnos.</p>
        </div>
        <button onClick={load} data-testid="refunds-refresh-btn"
                className="inline-flex items-center gap-1 text-xs text-gray-300 hover:text-white">
          <RefreshCw className="w-3 h-3" /> Actualizar
        </button>
      </div>

      <div className="flex flex-wrap gap-1 mb-4">
        {["pending_review", "auto_approved", "processed", "rejected", "all"].map(s => (
          <button key={s} onClick={() => setStatus(s)}
                  data-testid={`refunds-tab-${s}`}
                  className={`px-3 py-1.5 rounded-md text-xs font-semibold uppercase tracking-widest ${status === s ? "bg-[#F26A21] text-[#0B0F14]" : "border border-white/10 text-gray-300 hover:bg-white/5"}`}>
            {s === "all" ? "Todas" : STATUS_META[s]?.label || s}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="text-sm text-gray-400 inline-flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Cargando…</div>
      ) : items.length === 0 ? (
        <div className="rounded-2xl border border-white/10 bg-white/5 p-8 text-center text-sm text-gray-400" data-testid="refunds-empty">
          Sin solicitudes en este estado.
        </div>
      ) : (
        <div className="rounded-2xl border border-white/10 bg-white/5 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-white/[0.03]">
              <tr className="text-left text-[10px] uppercase tracking-widest text-gray-400">
                <th className="px-4 py-3">Orden</th>
                <th className="px-4 py-3">Alumno</th>
                <th className="px-4 py-3">Monto</th>
                <th className="px-4 py-3">Motivo</th>
                <th className="px-4 py-3">Progreso</th>
                <th className="px-4 py-3">Estado</th>
                <th className="px-4 py-3">Fecha</th>
                <th className="px-4 py-3 text-right">Acción</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/5">
              {items.map((r) => {
                const meta = STATUS_META[r.status] || {};
                return (
                  <tr key={r.id} data-testid={`refunds-row-${r.id}`}>
                    <td className="px-4 py-3 text-xs text-gray-300 font-mono">{r.order_number || r.order_id.slice(0, 8)}</td>
                    <td className="px-4 py-3">
                      <p className="text-white font-semibold text-sm">{r.user_name}</p>
                      <p className="text-[11px] text-gray-500">{r.user_email}</p>
                    </td>
                    <td className="px-4 py-3 font-mono text-white">${r.amount_mxn?.toLocaleString("es-MX")}</td>
                    <td className="px-4 py-3">
                      <p className="text-white text-xs">{REASON_LABEL[r.reason_category] || r.reason_category}</p>
                      <button onClick={() => setSelected(r)} className="text-[10px] text-[#F26A21] hover:underline">Ver notas</button>
                    </td>
                    <td className="px-4 py-3 text-xs text-white">{r.progress_snapshot_pct ?? "—"}%</td>
                    <td className="px-4 py-3">
                      <span className={`text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 rounded ${meta.cls || "bg-white/10 text-gray-300"}`}>
                        {meta.label || r.status}
                      </span>
                      {r.within_window === false && (
                        <span className="ml-1 inline-flex items-center gap-1 text-[10px] text-amber-300">
                          <AlertTriangle className="w-3 h-3" /> fuera de ventana
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-[11px] text-gray-400">{new Date(r.created_at).toLocaleDateString("es-MX")}</td>
                    <td className="px-4 py-3 text-right">
                      {r.status === "pending_review" && (
                        <>
                          <button onClick={() => resolve(r.id, "approve")}
                                  data-testid={`refunds-approve-${r.id}`}
                                  className="inline-flex items-center gap-1 rounded-md bg-emerald-500 text-[#0B0F14] px-2.5 py-1 text-xs font-semibold hover:brightness-110">
                            <CheckCircle2 className="w-3 h-3" /> Aprobar
                          </button>
                          <button onClick={() => resolve(r.id, "reject")}
                                  data-testid={`refunds-reject-${r.id}`}
                                  className="ml-1 inline-flex items-center gap-1 rounded-md border border-red-500/40 text-red-300 px-2.5 py-1 text-xs hover:bg-red-500/10">
                            <XCircle className="w-3 h-3" /> Rechazar
                          </button>
                        </>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {selected && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={() => setSelected(null)}>
          <div onClick={(e) => e.stopPropagation()}
               className="w-full max-w-lg rounded-2xl bg-[#111827] border border-white/10 p-6" data-testid="refunds-detail-modal">
            <p className="text-xs uppercase tracking-widest text-[#F26A21] font-bold">Solicitud</p>
            <h3 className="mt-1 text-lg font-bold text-white">{selected.order_number}</h3>
            <div className="mt-3 space-y-2 text-sm">
              <p><span className="text-gray-400">Alumno:</span> <span className="text-white">{selected.user_name}</span></p>
              <p><span className="text-gray-400">Motivo:</span> <span className="text-white">{REASON_LABEL[selected.reason_category]}</span></p>
              <p><span className="text-gray-400">Progreso:</span> <span className="text-white">{selected.progress_snapshot_pct}%</span></p>
              <p><span className="text-gray-400">Descripción:</span></p>
              <p className="text-white whitespace-pre-wrap">{selected.reason_text}</p>
              {selected.auto_approve_reason && (
                <p className="text-xs text-emerald-300">Auto-aprobación: {selected.auto_approve_reason}</p>
              )}
              {selected.admin_notes && (
                <p className="text-xs text-gray-300 mt-2"><strong>Notas admin:</strong> {selected.admin_notes}</p>
              )}
            </div>
            <div className="mt-6 text-right">
              <button onClick={() => setSelected(null)} className="text-sm text-gray-400 hover:text-white">Cerrar</button>
            </div>
          </div>
        </div>
      )}
    </StaffLayout>
  );
}
